import {
  CATEGORIES_BY_SCOPE,
  SCOPE_OPTIONS,
  SUB_CATEGORIES_BY_CATEGORY,
} from '../../../shared/constants';
import {
  MANAGEMENT_REPORTING_YEAR_BASELINE,
  reportingYearOptions,
} from '../../../shared/utils/reportingYear';

export const ACTION_PLAN_YEARS_AHEAD = 10;
export const ACTION_PLAN_END_YEARS_AHEAD = 15;

export const ACTION_PLAN_SCOPE_VALUES = [...SCOPE_OPTIONS, 'OTHER'];

export const ACTION_PLAN_SCOPE_LABELS = {
  SCOPE_1: 'Scope 1', SCOPE_2: 'Scope 2', SCOPE_3: 'Scope 3', OTHER: 'Other',
  NOT_APPLICABLE: 'Not applicable',
};

export const ACTION_PLAN_CONTRIBUTION_TYPES = [
  { value: 'EMISSIONS_REDUCTION', label: 'Emissions reduction' },
  { value: 'OTHER_CLIMATE_BENEFIT', label: 'Other climate benefit' },
];

export const ACTION_PLAN_CATEGORY_LABELS = {
  GASOLINE: 'Gasoline', ELECTRICITY: 'Electricity', CAT_1_1: 'Category 1.1',
  CAT_1_2: 'Category 1.2', CAT_1_3: 'Category 1.3', CAT_1_4: 'Category 1.4',
  CAT_1_5: 'Category 1.5', CAT_1_6: 'Category 1.6', CAT_1_7: 'Category 1.7',
  CAT_1_8: 'Category 1.8', CAT_1_9: 'Category 1.9', CAT_2: 'Category 2',
  CAT_3: 'Category 3', CAT_5: 'Category 5', CAT_6: 'Category 6',
  CAT_7: 'Category 7', CAT_8: 'Category 8', CONTRIBUTION: 'Contribution projects',
  OFFSET: 'Carbon offset programs', POLICY: 'General policy and behaviour',
};

export const ACTION_PLAN_STATUS_LABELS = {
  DRAFT: 'Draft', IN_PROGRESS: 'In progress', COMPLETED: 'Completed', CANCELLED: 'Cancelled',
};

export const ACTION_PLAN_CATEGORIES_BY_SCOPE = {
  ...CATEGORIES_BY_SCOPE,
  OTHER: ['CONTRIBUTION', 'OFFSET', 'POLICY'],
};

export const ACTION_PLAN_SUB_CATEGORIES_BY_CATEGORY = SUB_CATEGORIES_BY_CATEGORY;

export const boundaryTypeForScope = (scope) => {
  if (scope === 'SCOPE_1') return 'LEGAL_ENTITY';
  if (scope === 'SCOPE_2') return 'LOCATION';
  return 'COMPANY';
};

export const ACTION_PLAN_STATUS_VALUES = [
  'DRAFT',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
];

export const ACTION_PLAN_TRANSITIONS = Object.freeze({
  DRAFT: ['IN_PROGRESS', 'CANCELLED'],
  IN_PROGRESS: ['COMPLETED', 'CANCELLED'],
  CANCELLED: ['DRAFT'],
  COMPLETED: [],
});

export const normalizeActionPlan = (plan) => ({
  id: plan.id,
  title: plan.title,
  description: plan.description ?? '',
  actionType: plan.action_type,
  scope: plan.scope,
  category: plan.category,
  subCategory: plan.sub_category ?? null,
  boundary: plan.boundary
    ? {
      type: plan.boundary.type,
      refId: plan.boundary.ref_id ?? null,
      name: plan.boundary.name,
    }
    : null,
  startFiscalYear: Number(plan.start_fiscal_year),
  endFiscalYear: Number(plan.end_fiscal_year),
  status: plan.status,
  planningEstimates: (Array.isArray(plan.planning_estimates) ? plan.planning_estimates : [])
    .map(estimate => ({
      fiscalYear: Number(estimate.fiscal_year),
      reductionTco2ePerYear: estimate.reduction_tco2e_per_year ?? null,
      estimatedActivity: estimate.estimated_activity ?? null,
      activityUnit: estimate.activity_unit ?? null,
      itemKey: estimate.item_key ?? null,
      basisNote: estimate.basis_note ?? null,
      emissionFactor: estimate.emission_factor ?? null,
    })),
  annualResults: (Array.isArray(plan.annual_results) ? plan.annual_results : []).map(normalizeActionPlanResult),
  accountingClassification: plan.accounting_classification,
  aiRecommendationId: plan.ai_recommendation_id ?? null,
  createdAt: plan.created_at,
  updatedAt: plan.updated_at,
});

export const normalizeActionPlanResult = result => ({
  id: result.id ?? result._id ?? null,
  actionId: result.actionId ?? result.action_id,
  fiscalYear: Number(result.fiscalYear ?? result.fiscal_year),
  inputMode: result.inputMode ?? result.input_mode ?? result.method ?? null,
  activityValue: result.activityValue ?? result.activity_value ?? null,
  activityUnit: result.activityUnit ?? result.activity_unit ?? null,
  reportedTco2e: result.reportedTco2e ?? result.reported_tco2e
    ?? result.actual_tco2e ?? result.reported_impact_tco2e ?? null,
  basisNote: result.basisNote ?? result.basis_note ?? result.note ?? '',
  status: result.status ?? (result.isVerified ? 'VERIFIED' : 'DRAFT'),
  evidenceFiles: (Array.isArray(result.evidenceFiles ?? result.evidence_files)
    ? (result.evidenceFiles ?? result.evidence_files) : [])
    .map(normalizeEvidenceFile),
});

export const normalizeEvidenceFile = item => ({
  id: item.id ?? item.evidenceId ?? item.evidence_id,
  name: item.fileName ?? item.file_name ?? item.name ?? '',
  contentType: item.contentType ?? item.content_type ?? null,
  sizeBytes: item.sizeBytes ?? item.size_bytes ?? null,
  sha256: item.sha256 ?? null,
  createdAt: item.createdAt ?? item.created_at ?? null,
});

export const normalizeActionPlanResultInputOptions = options => ({
  activityBasedAvailable: Boolean(options?.activityBasedAvailable ?? options?.activity_based_available),
  activityUnit: options?.activityUnit ?? options?.activity_unit ?? null,
  factorValue: options?.factorValue ?? options?.factor_value ?? null,
  factorUnit: options?.factorUnit ?? options?.factor_unit ?? null,
  businessName: options?.businessName ?? options?.business_name ?? null,
  effectiveYear: options?.effectiveYear ?? options?.effective_year ?? null,
  reason: options?.reason ?? null,
});

export const serializeActionPlanResult = result => {
  const isActivityBased = result.inputMode === 'ACTIVITY_BASED';
  return {
    method: result.inputMode,
    actual_tco2e: !isActivityBased && result.reportedTco2e !== '' && result.reportedTco2e != null
      ? String(result.reportedTco2e) : null,
    activity_value: isActivityBased && result.activityValue !== '' && result.activityValue != null
      ? String(result.activityValue) : null,
    note: result.basisNote || null,
  };
};

export const serializeActionPlanDraft = (draft) => ({
  title: draft.title,
  description: draft.description || null,
  action_type: draft.actionType,
  scope: draft.actionType === 'CARBON_CREDIT_RETIREMENT' ? 'NOT_APPLICABLE' : draft.scope,
  category: draft.actionType === 'CARBON_CREDIT_RETIREMENT' ? 'NOT_APPLICABLE' : draft.category,
  sub_category: draft.actionType === 'CARBON_CREDIT_RETIREMENT' ? null : (draft.subCategory || null),
  boundary: {
    type: draft.boundaryType,
    ref_id: draft.boundaryRefId || null,
  },
  start_fiscal_year: Number(draft.startFiscalYear),
  end_fiscal_year: Number(draft.endFiscalYear),
  planning_estimates: (Array.isArray(draft.planningEstimates) ? draft.planningEstimates : [])
    .map(estimate => ({
      fiscal_year: Number(estimate.fiscalYear),
      reduction_tco2e_per_year: estimate.reductionTco2ePerYear !== '' && estimate.reductionTco2ePerYear != null
        ? String(estimate.reductionTco2ePerYear)
        : null,
       basis_note: estimate.basisNote || null,
    })),
  ai_recommendation_id: draft.aiRecommendationId || null,
});

export const estimateForFiscalYear = (plan, fiscalYear) => {
  const year = Number(fiscalYear);
  return (plan.planningEstimates || []).find(estimate => estimate.fiscalYear === year) || null;
};

export const startFiscalYearOptions = ({ includeYears = [], now } = {}) => (
  reportingYearOptions({
    includeYears: [MANAGEMENT_REPORTING_YEAR_BASELINE, ...includeYears],
    now,
    yearsAhead: ACTION_PLAN_YEARS_AHEAD,
  })
);

export const endFiscalYearOptions = ({ startFiscalYear, includeYears = [], now } = {}) => (
  reportingYearOptions({
    includeYears: [MANAGEMENT_REPORTING_YEAR_BASELINE, ...includeYears],
    now,
    yearsAhead: ACTION_PLAN_END_YEARS_AHEAD,
  })
    .filter(year => year >= Number(startFiscalYear))
);

export const planAppliesToFiscalYear = (plan, fiscalYear) => {
  const year = Number(fiscalYear);
  return year >= plan.startFiscalYear && year <= plan.endFiscalYear;
};

export const commonPlanStatusOptions = (plans) => {
  const common = new Set(ACTION_PLAN_STATUS_VALUES);
  plans.forEach(plan => {
    const allowed = ACTION_PLAN_TRANSITIONS[plan.status] || [];
    const allowedSet = new Set(allowed);
    [...common].forEach(status => {
      if (!allowedSet.has(status)) common.delete(status);
    });
  });
  return [...common];
};
